/*
 * deal-completion_askmario.js
 * ---------------------------
 * Opens the "complete your deal" picker drawer from the prompt that
 * snippets/cart-deal-progress_askmario.liquid renders above eligible cart lines.
 *
 * The drawer is a plain theme DrawerComponent — show()/hide() and the slide are the
 * theme's own. Only two things are special:
 *
 *  - The CTA is bound with a delegated document listener, because cart.js replaces
 *    #CartDrawerBody innerHTML on every cart update while ModalComponent snapshots
 *    [aria-controls] only once, so directly-bound buttons would go dead.
 *  - The picker closes whenever the cart drawer closes, so it can never be left
 *    stranded on screen on its own.
 */

(function () {
  'use strict';

  const SELECTORS = {
    drawer: '#DealPickerDrawer',
    cartDrawer: '#CartDrawer',
    cta: '[data-deal-cta]',
    results: '[data-deal-picker-results]',
    heading: '[data-deal-picker-heading]',
    headline: '[data-deal-picker-headline]',
    statusTitle: '[data-deal-picker-status-title]',
    prompt: '.dealprog',
    close: '[data-deal-picker-close]',
    list: '[data-deal-picker-list]',
    item: '[data-deal-picker-item]',
  };

  const STRINGS = {
    empty: 'No matching products are available right now.',
    error: "We couldn't load those products. Please try again.",
    browse: 'Browse the collection',
  };

  const cache = new Map();

  // How many matching products the picker actually shows. The section deliberately
  // scans far more than this so the metafield-group filter has something to find.
  const DISPLAY_LIMIT = 12;

  function getDrawer() {
    return document.querySelector(SELECTORS.drawer);
  }

  /* ---------------------------------------------------------------- *
   * Custom element — a stock drawer, minus the body scroll lock
   * ---------------------------------------------------------------- */

  function defineDrawer() {
    if (customElements.get('deal-picker-drawer')) return true;
    if (typeof DrawerComponent === 'undefined') return false;

    class DealPickerDrawer extends DrawerComponent {
      // The cart drawer already owns the scroll lock and stays open behind us;
      // taking it here would release it when only the picker closes.
      get isLockingNeeded() {
        return false;
      }

      // Stay put in the section rather than being moved to <body>, matching
      // CartDrawer's own override.
      get requiresBodyAppended() {
        return false;
      }
    }

    customElements.define('deal-picker-drawer', DealPickerDrawer);
    return true;
  }

  /* ---------------------------------------------------------------- *
   * Product list
   * ---------------------------------------------------------------- */

  function emptyMarkup(collectionHandle) {
    const root = window.FoxTheme?.routes?.root_url || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const link = collectionHandle
      ? `<a class="btn btn--secondary" href="${base}collections/${collectionHandle}"><span class="btn__text">${STRINGS.browse}</span></a>`
      : '';
    return `<div class="deal-picker__state"><p>${STRINGS.empty}</p>${link}</div>`;
  }

  /*
   * Mirror the cart-line prompt's state into the picker's status band, so the two
   * never contradict each other — "You're almost there..." sitting above a completed
   * deal was the visible symptom. Everything is read off the prompt element, which
   * the cart drawer re-renders server-side on every cart change, so this stays right
   * whether the picker was opened on a completed deal or the deal completed while it
   * was open.
   */
  function syncStatus(drawer, prompt) {
    if (!drawer || !prompt) return;

    const title = drawer.querySelector(SELECTORS.statusTitle);
    const heading = drawer.querySelector(SELECTORS.heading);
    const text = prompt.querySelector('.dealprog__text');

    if (title && prompt.dataset.dealStatusTitle) {
      title.textContent = prompt.dataset.dealStatusTitle;
    }
    if (heading && text) heading.textContent = text.textContent.trim();

    drawer.classList.toggle('deal-picker--complete', prompt.dataset.dealComplete === 'true');
  }

  /*
   * Show the "checking out with an unfinished deal" warning above the checkout
   * button. Driven off the per-line prompts already rendered in the drawer rather
   * than recomputing the deal maths, so it can never disagree with what the customer
   * is being told a few centimetres higher up. Any prompt still asking for units
   * means something is unfinished — including a cart that already has one set
   * applied and is part-way to another.
   */
  function syncCheckoutWarning() {
    const cartDrawer = document.querySelector(SELECTORS.cartDrawer);
    if (!cartDrawer) return;

    const warning = cartDrawer.querySelector('[data-deal-warning]');
    if (!warning) return;

    const pending = Array.from(cartDrawer.querySelectorAll(SELECTORS.prompt)).some(
      (p) => parseInt(p.dataset.dealRemaining, 10) > 0
    );

    warning.hidden = !pending;
  }

  /*
   * Mobile only: once the last outstanding deal is completed FROM the picker, slide
   * the picker away so the shopper lands back on the cart drawer and can check out.
   * On a phone the picker covers the cart completely, so leaving it up after the job
   * is done hides the checkout button behind a drawer with nothing left to do in it.
   * Desktop keeps it open — the cart is still visible alongside, and closing it
   * under the cursor would be the surprise rather than the convenience.
   *
   * Only closes the picker, never the cart drawer: closeBothInSequence() is for the
   * shopper dismissing the cart itself, which is a different intent.
   */
  const MOBILE_MAX_WIDTH = 767;

  function allDealsComplete() {
    const cartDrawer = document.querySelector(SELECTORS.cartDrawer);
    if (!cartDrawer) return false;

    const prompts = Array.from(cartDrawer.querySelectorAll(SELECTORS.prompt));
    // No prompts at all means nothing to judge — do not treat that as "complete",
    // or a drawer that has not re-rendered yet would close the picker mid-flight.
    if (!prompts.length) return false;

    return prompts.every((p) => !(parseInt(p.dataset.dealRemaining, 10) > 0));
  }

  function closePickerIfAllDoneOnMobile() {
    if (window.innerWidth > MOBILE_MAX_WIDTH) return;

    const drawer = getDrawer();
    if (!drawer || !drawer.hasAttribute('open')) return;
    if (!allDealsComplete()) return;

    closePicker();
  }

  function currentPrompt(drawer) {
    const group = drawer && drawer.dataset.dealGroup;
    if (!group) return null;
    return document.querySelector(`${SELECTORS.prompt}[data-deal-group="${CSS.escape(group)}"]`);
  }

  /*
   * URLs to search for this deal's products. The metafield group is what decides
   * membership — these are only the candidate POOLS that check runs against. ALL of
   * them are searched and the matches merged: a single collection rarely holds the
   * whole group (cotton-bath-sheet has 4 products spread across several), so
   * stopping at the first hit under-reports the deal.
   */
  function buildUrls(cta) {
    const root = window.FoxTheme?.routes?.root_url || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const sectionId = 'deal-picker_askmario';
    const urls = [];

    (cta.dataset.dealCollections || cta.dataset.dealCollection || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean)
      .forEach((handle) => urls.push(`${base}collections/${handle}?section_id=${sectionId}`));

    if (cta.dataset.dealProductId) {
      const url = cta.dataset.dealRecommendationsUrl || '/recommendations/products';
      urls.push(
        `${url}?section_id=${sectionId}&product_id=${cta.dataset.dealProductId}&limit=12&intent=related`
      );
    }

    return urls;
  }

  // Fetch one candidate and return the cards whose metafield group matches — the
  // same test the Rust function uses to decide what gets discounted.
  async function fetchMatching(url, group) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const scope = doc.querySelector('.shopify-section') || doc;
    const list = scope.querySelector(SELECTORS.list);
    if (!list) return [];

    return Array.from(list.querySelectorAll(SELECTORS.item)).filter(
      (item) => !group || (item.dataset.dealItemGroup || '') === group
    );
  }

  // Search every candidate in parallel and merge the results, so a group spread
  // across several collections is reported in full.
  async function collectMatches(urls, group) {
    const settled = await Promise.all(
      urls.map((url) =>
        fetchMatching(url, group).catch((err) => {
          console.warn('[deal-completion] candidate failed', url, err);
          return [];
        })
      )
    );

    const byProduct = new Map();
    settled.flat().forEach((item) => {
      const link = item.querySelector('a[href*="/products/"]');
      const key = link ? link.getAttribute('href').split('?')[0] : item.outerHTML;
      if (!byProduct.has(key)) byProduct.set(key, item);
    });

    let matches = Array.from(byProduct.values());

    /*
     * Prefer products the customer hasn't got yet. But some deals ("any 2 for R…"
     * where a single product carries the group) are completed by a second unit of
     * the item already in the cart — so when there is no alternative, keep the
     * in-cart product rather than showing an empty picker.
     */
    const alternatives = matches.filter((i) => i.dataset.dealInCart !== 'true');
    if (alternatives.length) matches = alternatives;

    return matches.slice(0, DISPLAY_LIMIT);
  }

  async function renderPicker(cta, drawer) {
    const results = drawer.querySelector(SELECTORS.results);
    if (!results) return;

    const headline = drawer.querySelector(SELECTORS.headline);

    // headline = the offer; the status band below it tracks progress
    if (headline) headline.textContent = cta.dataset.dealHeadline || '';
    drawer.dataset.dealGroup = cta.dataset.dealGroup || '';
    syncStatus(drawer, cta.closest(SELECTORS.prompt));

    // buy_x_get_y ships its eligible products server-rendered in a <template>,
    // because bonus_handles names them exactly — no fetch needed.
    if (cta.dataset.dealSource === 'template') {
      const tpl = document.getElementById(cta.dataset.dealTemplate);
      if (tpl && tpl.content) {
        results.innerHTML = '';
        results.appendChild(tpl.content.cloneNode(true));
      } else {
        results.innerHTML = emptyMarkup('');
      }
      return;
    }

    const urls = buildUrls(cta);
    if (!urls.length) {
      results.innerHTML = emptyMarkup(cta.dataset.dealCollection);
      return;
    }

    // Cache per group, not just per URL: one collection can legitimately be the
    // source for more than one deal, and each keeps a different filtered list.
    const group = cta.dataset.dealGroup || '';
    const cacheKey = `${urls.join('|')}::${group}`;

    if (cache.has(cacheKey)) {
      results.innerHTML = cache.get(cacheKey);
      return;
    }

    results.innerHTML = '<div class="deal-picker__state"><span class="deal-picker__spinner"></span></div>';

    try {
      const matches = await collectMatches(urls, group);
      console.info(`[deal-completion] group "${group}": ${matches.length} product(s) found`);

      let html;
      if (matches.length) {
        const list = document.createElement('div');
        list.className = 'deal-picker__list';
        list.setAttribute('data-deal-picker-list', '');
        matches.forEach((item) => list.appendChild(item));
        html = list.outerHTML;
      } else {
        html = emptyMarkup(cta.dataset.dealCollection);
      }

      cache.set(cacheKey, html);
      results.innerHTML = html;
    } catch (error) {
      console.warn('[deal-completion] could not load picker products', error);
      results.innerHTML = `<div class="deal-picker__state"><p>${STRINGS.error}</p></div>`;
    }
  }

  /* ---------------------------------------------------------------- *
   * Open / close
   * ---------------------------------------------------------------- */

  // Line the picker's header rule up with the one under "Your cart". The cart
  // header's height shifts with the message banner and title, so measure it rather
  // than hardcode a number; the CSS derives the headline height from this.
  function matchHeaderRule(drawer) {
    const cartDrawer = document.querySelector(SELECTORS.cartDrawer);
    const cartHeader = cartDrawer && cartDrawer.querySelector('.drawer__header-inner');
    const cartPanel = cartDrawer && cartDrawer.querySelector('.drawer__inner');
    if (!cartHeader || !cartPanel) return;

    const offset = cartHeader.getBoundingClientRect().bottom - cartPanel.getBoundingClientRect().top;
    if (offset > 0) drawer.style.setProperty('--dp-header-offset', `${Math.round(offset)}px`);
  }

  // Closing the cart with the picker open cascades: the picker slides out first,
  // then the cart follows. One gesture, two staggered animations — the panels never
  // move together. 250ms is roughly 40% through the theme's 0.6s drawer slide, which
  // reads as a sequence without feeling slow.
  const CASCADE_DELAY = 250;
  let cascadeTimer = null;

  function openPicker(cta) {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.show !== 'function') return;

    // Cancel a pending cascade, so reopening right after a close cannot pull the
    // cart drawer shut underneath the picker.
    window.clearTimeout(cascadeTimer);

    document.body.classList.add('deal-picker-open');
    matchHeaderRule(drawer);
    renderPicker(cta, drawer);
    drawer.show(cta, true);
  }

  function closePicker() {
    const drawer = getDrawer();
    if (!drawer || !drawer.hasAttribute('open')) return;
    if (typeof drawer.hide === 'function') drawer.hide();
    document.body.classList.remove('deal-picker-open');
  }

  function closeBothInSequence() {
    closePicker();

    window.clearTimeout(cascadeTimer);
    cascadeTimer = window.setTimeout(() => {
      const cartDrawer = document.querySelector(SELECTORS.cartDrawer);
      if (cartDrawer && cartDrawer.hasAttribute('open') && typeof cartDrawer.hide === 'function') {
        cartDrawer.hide();
      }
    }, CASCADE_DELAY);
  }

  /* ---------------------------------------------------------------- *
   * Wiring
   * ---------------------------------------------------------------- */

  let initialised = false;

  function init(isRetry) {
    if (!defineDrawer()) {
      if (isRetry) {
        console.warn('[deal-completion] DrawerComponent unavailable — feature disabled');
        return;
      }
      document.addEventListener('DOMContentLoaded', () => init(true), { once: true });
      return;
    }

    if (initialised) return;
    initialised = true;

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      const cta = target.closest(SELECTORS.cta);
      if (cta) {
        event.preventDefault();
        openPicker(cta);
        return;
      }

      if (target.closest(SELECTORS.close)) {
        event.preventDefault();
        closePicker();
      }
    });

    // Escape dismisses the topmost drawer only, as it should: one press closes the
    // picker, a second closes the cart. Capture phase so it beats ModalComponent's
    // own bubble-phase Escape listener, which would otherwise close both at once.
    document.addEventListener(
      'keyup',
      (event) => {
        if (event.code !== 'Escape') return;
        const drawer = getDrawer();
        if (!drawer || !drawer.hasAttribute('open')) return;
        event.stopPropagation();
        closePicker();
      },
      true
    );

    // The cart drawer's close button and backdrop (both carry
    // aria-controls="CartDrawer") close everything in one gesture — picker first,
    // cart just behind it. Propagation is stopped so the theme's own handler cannot
    // close the cart immediately, which would drop both panels simultaneously.
    document.addEventListener(
      'click',
      (event) => {
        const drawer = getDrawer();
        if (!drawer || !drawer.hasAttribute('open')) return;

        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (target.closest(SELECTORS.drawer)) return; // the picker's own X, handled below

        if (target.closest('[aria-controls="CartDrawer"]')) {
          event.preventDefault();
          event.stopPropagation();
          closeBothInSequence();
        }
      },
      true
    );

    // Never leave the picker stranded once the cart drawer it belongs to is gone.
    const cartDrawer = document.querySelector(SELECTORS.cartDrawer);
    if (cartDrawer) {
      new MutationObserver(() => {
        if (!cartDrawer.hasAttribute('open')) closePicker();
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });

      // Any cart change re-renders the drawer's body and footer server-side. Watch
      // for those swaps to refresh the picker's status band and the checkout warning.
      new MutationObserver(() => {
        const drawer = getDrawer();
        if (drawer && drawer.hasAttribute('open')) syncStatus(drawer, currentPrompt(drawer));
        syncCheckoutWarning();
        closePickerIfAllDoneOnMobile();
      }).observe(cartDrawer, { childList: true, subtree: true });

      syncCheckoutWarning();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(false), { once: true });
  } else {
    init(false);
  }
})();
